import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, TrendingUp, Brain, Radio, Music, Timer, Headphones, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import PremiumCard from '@/components/PremiumCard';
import PremiumButton from '@/components/PremiumButton';
import { PREMIUM_PRICING } from '@/config/pricingConfig';
import LaunchOfferBadge from '@/components/LaunchOfferBadge';

const Premium = ({ t }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showComingSoon, setShowComingSoon] = React.useState(false);
  
  // Language detection
  const lang = localStorage.getItem('hearen_lang') || 'fr';
  const isEnglish = lang === 'en' || t?.locale === 'en-US';

  const features = [
    {
      icon: Headphones,
      title: 'Davantage de paysages sonores',
      description: 'Explorez plus d’ambiances et de moments d’écoute.'
    },
    {
      icon: Sparkles,
      title: 'Presence guidée par Hera',
      description: 'Des expériences Presence plus riches et plus immersives.'
    },
    {
      icon: Music,
      title: 'Playlists personnelles',
      description: 'Organisez vos moments d’écoute plus librement.'
    },
    {
      icon: Brain,
      title: 'Repères de progression',
      description: 'Suivez votre rythme d’écoute au fil du temps.'
    },
    {
      icon: TrendingUp,
      title: 'Une écoute plus personnelle',
      description: 'Des suggestions et ajustements plus contextualisés.'
    },
    {
      icon: Timer,
      title: 'Écoute prolongée',
      description: 'Prenez le temps dont vous avez besoin.'
    }
  ];

  const plans = [
    {
      id: 'monthly',
      name: isEnglish ? PREMIUM_PRICING.monthly.labelEn : PREMIUM_PRICING.monthly.label,
      price: isEnglish ? PREMIUM_PRICING.monthly.priceEn : PREMIUM_PRICING.monthly.price,
      period: isEnglish ? PREMIUM_PRICING.monthly.periodEn : PREMIUM_PRICING.monthly.period,
      popular: PREMIUM_PRICING.monthly.popular
    },
    {
      id: 'yearly',
      name: isEnglish ? PREMIUM_PRICING.yearly.labelEn : PREMIUM_PRICING.yearly.label,
      price: isEnglish ? PREMIUM_PRICING.yearly.priceEn : PREMIUM_PRICING.yearly.price,
      period: isEnglish ? PREMIUM_PRICING.yearly.periodEn : PREMIUM_PRICING.yearly.period,
      save: isEnglish ? PREMIUM_PRICING.yearly.saveEn : PREMIUM_PRICING.yearly.save,
      popular: PREMIUM_PRICING.yearly.popular,
      // Monthly equivalent badge for yearly plan
      monthlyEquivalent: isEnglish ? PREMIUM_PRICING.yearly.monthlyEquivalentEn : PREMIUM_PRICING.yearly.monthlyEquivalent,
      monthlyEquivalentPeriod: isEnglish ? PREMIUM_PRICING.yearly.monthlyEquivalentPeriodEn : PREMIUM_PRICING.yearly.monthlyEquivalentPeriod,
      billedYearly: isEnglish ? PREMIUM_PRICING.yearly.billedYearlyEn : PREMIUM_PRICING.yearly.billedYearly
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#151A24] via-[#1D2333] to-[#151A24] pb-32 relative overflow-hidden">
      {/* Decorative halos */}
      <div 
        className="absolute top-0 left-1/4 w-[600px] h-[600px] opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(120, 110, 255, 0.12) 0%, transparent 70%)'
        }}
      />
      <div 
        className="absolute bottom-0 right-1/4 w-[500px] h-[500px] opacity-15 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(159, 134, 255, 0.3) 0%, transparent 70%)',
          filter: 'blur(90px)'
        }}
      />
      
      {/* Light streak horizontal */}
      <div 
        className="absolute top-1/3 left-0 right-0 h-px opacity-10"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(106, 79, 255, 0.6) 50%, transparent 100%)'
        }}
      />
      
      {/* Header */}
      <div className="relative overflow-hidden">
        {/* Gradient glow background */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-pink-900/10 to-transparent" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'radial-gradient(circle at 50% 0%, rgba(106, 79, 255, 0.4) 0%, transparent 50%)'
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 pt-16 pb-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-white/60" />
            <span className="text-white/65 text-sm font-medium">
              Hearen Premium
            </span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">
            {t?.premiumTitle || 'Aller un peu plus loin avec Hearen'}
          </h1>
          
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            {t?.premiumSubtitle || "Une expérience d’écoute plus profonde, plus personnelle et plus immersive."}
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <PremiumCard
                key={index}
                hoverable={false}
                className="group"
              >
                <div 
                  className="w-12 h-12 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center mb-4 transition-colors"
                  style={{ boxShadow: '0 0 20px rgba(106, 79, 255, 0.3)' }}
                >
                  <Icon className="w-6 h-6 text-white/70" />
                </div>
                
                <h3 className="text-white/96 font-semibold text-lg mb-2">
                  {feature.title}
                </h3>
                
                <p className="text-white/60 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </PremiumCard>
            );
          })}
        </div>
      </div>

      {/* Separator Premium */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* Pricing Plans */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white/96 text-center mb-8">
          {t?.choosePlan || 'Hearen Premium'}
        </h2>
        
        {/* Launch Offer Banner */}
        <div className="mb-10">
          <LaunchOfferBadge 
            isEnglish={t?.locale === 'en-US'} 
            variant="banner"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white/5 backdrop-blur-md border rounded-3xl p-8 transition-all duration-300 ${
                plan.popular
                  ? 'border-white/15 bg-white/[0.07]'
                  : 'border-white/10 hover:border-purple-500/30'
              }`}
              style={{
                boxShadow: '0 0 24px rgba(120,110,255,0.08)'
              }}
            >
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                {plan.save && (
                  <span className="inline-block bg-green-500/20 text-green-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                    {plan.save}
                  </span>
                )}
              </div>
              
              <div className="text-center mb-8">
                {/* Price display - handle both FR (7,90€) and EN (€7.90) formats */}
                {isEnglish ? (
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-white">{plan.price.split('.')[0]}</span>
                    <span className="text-3xl font-bold text-white">.{plan.price.split('.')[1]}</span>
                  </div>
                ) : (
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-white">{plan.price.split(',')[0]}</span>
                    <span className="text-3xl font-bold text-white">,{plan.price.split(',')[1]}</span>
                  </div>
                )}
                <span className="text-white/60 text-sm">{plan.period}</span>
                
                {/* Monthly equivalent badge for yearly plan */}
                {plan.id === 'yearly' && plan.monthlyEquivalent && (
                  <div className="mt-3">
                    <div className="inline-flex items-center gap-1 bg-purple-500/20 border border-purple-500/30 rounded-full px-3 py-1.5">
                      <span className="text-purple-300 text-sm font-semibold">
                        {plan.monthlyEquivalent}{plan.monthlyEquivalentPeriod}
                      </span>
                    </div>
                    <p className="text-white/40 text-xs mt-1">{plan.billedYearly}</p>
                  </div>
                )}
              </div>
              
              <PremiumButton
                onClick={() => setShowComingSoon(true)}
                variant={plan.popular ? 'primary' : 'secondary'}
                size="lg"
                fullWidth
              >
                {t?.upgradeNow || 'Passer à Premium'}
              </PremiumButton>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits List */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            {t?.whatsIncluded || 'Tout Ce Qui Est Inclus'}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              t?.benefit1 || 'Accès illimité à tous les sons',
              t?.benefit2 || 'Toutes les expériences Presence',
              t?.benefit3 || 'Playlists personnalisées illimitées',
              t?.benefit4 || 'Coach Hearen avancé',
              t?.benefit5 || 'Statistiques détaillées',
              t?.benefit6 || 'Séances sans limite de durée',
              t?.benefit7 || 'Nouvelles fonctionnalités en priorité',
              t?.benefit8 || 'Support prioritaire'
            ].map((benefit, index) => (
              <div key={index} className="flex items-start gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-white/35 flex-shrink-0 mt-2" />
                <span className="text-white/80 text-sm">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Preview */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h3 className="text-2xl font-bold text-white mb-6 text-center">
          {t?.faqTitle || 'Questions Fréquentes'}
        </h3>
        
        <div className="space-y-4">
          {[
            {
              q: t?.faq1Q || 'Puis-je annuler à tout moment ?',
              a: t?.faq1A || 'Oui, vous pouvez annuler votre abonnement à tout moment depuis les paramètres de votre compte.'
            },
            {
              q: t?.faq3Q || 'Que se passe-t-il si j\'annule ?',
              a: t?.faq3A || 'Vous conservez l\'accès Premium jusqu\'à la fin de votre période de facturation, puis votre compte repasse en version gratuite.'
            }
          ].map((faq, index) => (
            <div
              key={index}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all"
            >
              <h4 className="text-white font-semibold mb-2">{faq.q}</h4>
              <p className="text-white/60 text-sm">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Separator Premium */}
      <div className="max-w-6xl mx-auto px-4 mb-16">
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* CTA Footer */}
      <div className="max-w-4xl mx-auto px-4 pb-16 text-center">
        <PremiumCard hoverable={false} className="p-12 relative overflow-hidden">
          {/* Animated glow */}
          <div 
            className="absolute inset-0 opacity-50 animate-pulse"
            style={{
              background: 'radial-gradient(circle at 50% 50%, rgba(106, 79, 255, 0.15) 0%, transparent 60%)',
              animation: 'pulse 3s ease-in-out infinite'
            }}
          />
          
          <div className="relative z-10">
            <div 
              className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6"
              style={{ boxShadow: '0 0 30px rgba(106, 79, 255, 0.5)' }}
            >
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            
            <h3 className="text-3xl font-bold text-white/96 mb-4">
              {t?.readyToUpgrade || 'Prêt à Passer à Premium ?'}
            </h3>
            
            <p className="text-white/70 text-base mb-8 max-w-2xl mx-auto leading-relaxed">
              {t?.upgradeMessage || 'Rejoignez des milliers d\'utilisateurs qui ont déjà amélioré leur bien-être auditif avec Hearen Premium.'}
            </p>
            
            <PremiumButton
              variant="primary"
              size="lg"
              icon={<ArrowRight className="w-5 h-5" />}
              onClick={() => setShowComingSoon(true)}
            >
              {t?.startNow || 'Commencer Maintenant'}
            </PremiumButton>
          </div>
        </PremiumCard>
      </div>
      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="bg-[#1a1f35] border border-white/10 rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-600/20 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-violet-400" />
            </div>
            <h3 className="text-white text-lg font-semibold mb-3">
              {isEnglish ? 'Hearen Premium is coming soon.' : 'Hearen Premium arrive bientôt.'}
            </h3>
            <p className="text-white/60 text-sm mb-6">
              {isEnglish ? 'Thank you for testing the current version.' : 'Merci de tester la version actuelle.'}
            </p>
            <button
              onClick={() => setShowComingSoon(false)}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium text-sm hover:brightness-110 transition-all"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Premium;
